/**
 * Monte Carlo simulation engine — extracted from model.ts for Workstream B.
 *
 * Contains the core iteration logic, statistical helpers (percentile,
 * histogramBins), and chunked async runner. The interfaces (MonteCarloVariable,
 * MonteCarloResult) remain in model.ts because they depend on Scenario/ModelResult.
 *
 * computeCore is injected via a callback so this module has zero dependency on
 * model.ts internals.
 */

import type { MonteCarloResult, MonteCarloVariable } from "../model";
import type { Scenario, ModelResult } from "../types";
import {
  OPERATOR_AFFORDABILITY_CAUTION,
  OPERATOR_AFFORDABILITY_CEILING,
} from "../decision-thresholds";
export {
  OPERATOR_AFFORDABILITY_CAUTION,
  OPERATOR_AFFORDABILITY_CEILING,
} from "../decision-thresholds";

// ---------- batch size ----------
export const MC_BATCH_SIZE = 200;

// ---------- math helpers ----------
const sum = (arr: number[]): number =>
  Array.isArray(arr)
    ? arr.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0)
    : 0;

const clone = <T>(o: T): T => structuredClone(o);

export function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return Number.NaN;
  const idx = Math.max(
    0,
    Math.min(sorted.length - 1, Math.floor((p / 100) * (sorted.length - 1))),
  );
  return sorted[idx];
}

export function histogramBins(
  sorted: number[],
  bins: number,
): { counts: number[]; edges: number[] } {
  if (sorted.length === 0)
    return {
      counts: new Array(bins).fill(0),
      edges: new Array(bins + 1).fill(0),
    };
  const min = sorted[0];
  const max = sorted.at(-1)!;
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return {
      counts: new Array(bins).fill(0),
      edges: new Array(bins + 1).fill(min),
    };
  }
  const width = (max - min) / bins;
  const edges = Array.from({ length: bins + 1 }, (_, i) => min + i * width);
  const counts = new Array(bins).fill(0);
  for (const v of sorted) {
    let bucket = Math.floor((v - min) / width);
    if (bucket >= bins) bucket = bins - 1;
    if (bucket < 0) bucket = 0;
    counts[bucket]++;
  }
  return { counts, edges };
}

// ---------- per-iteration runner ----------
export interface MonteCarloIterationResult {
  devIrr: number;
  npv: number;
  hurdleMet: boolean;
  breachCaution: boolean;
  breachThreshold: boolean;
  ebitdaNegY1: boolean;
  ebitdaNegPostRamp: boolean;
  dscrBreach: boolean;
}

export function runMonteCarloIteration(
  scenario: Scenario,
  variables: MonteCarloVariable[],
  computeCore: (s: Scenario) => ModelResult,
  baseScenarioGearedHurdle: number,
): MonteCarloIterationResult {
  const copy = clone(scenario);
  for (const v of variables) {
    const factor = 1 + (Math.random() * 2 - 1) * v.range;
    v.apply(copy, factor);
  }
  const r = computeCore(copy);
  const postRampIdx = Math.min(
    copy.assumptions.rampYears ?? 5,
    r.years.length - 1,
  );
  return {
    devIrr: r.kpis.developerPrimaryGearedIrr,
    npv: r.kpis.reddamNpv,
    hurdleMet:
      Number.isFinite(r.kpis.developerPrimaryGearedIrr) &&
      Number.isFinite(baseScenarioGearedHurdle) &&
      r.kpis.developerPrimaryGearedIrr >= baseScenarioGearedHurdle,
    breachCaution: r.kpis.rentPctRevenueYear1 > OPERATOR_AFFORDABILITY_CAUTION,
    breachThreshold:
      r.kpis.rentPctRevenueYear1 > OPERATOR_AFFORDABILITY_CEILING,
    ebitdaNegY1: r.kpis.yearOneEbitda < 0,
    ebitdaNegPostRamp:
      postRampIdx >= 0 && r.years[postRampIdx].reddamEbitda < 0,
    dscrBreach: r.developer.dscrStatus !== "na" && r.developer.dscr < 1.25,
  };
}

// ---------- result aggregation ----------
function aggregateResults(
  iterations: number,
  devIrrs: number[],
  npvs: number[],
  hurdleMet: number,
  breachCaution: number,
  breachThreshold: number,
  ebitdaNegY1: number,
  ebitdaNegPostRamp: number,
  dscrBreach: number,
  baseScenarioGearedHurdle: number,
): MonteCarloResult {
  devIrrs.sort((a, b) => a - b);
  npvs.sort((a, b) => a - b);

  const devHist = histogramBins(devIrrs, 20);
  const npvHist = histogramBins(npvs, 20);

  return {
    iterations,
    developerGearedIrr: {
      mean: devIrrs.length > 0 ? sum(devIrrs) / devIrrs.length : Number.NaN,
      p5: percentile(devIrrs, 5),
      p25: percentile(devIrrs, 25),
      p50: percentile(devIrrs, 50),
      p75: percentile(devIrrs, 75),
      p95: percentile(devIrrs, 95),
      histogram: devHist.counts,
      bins: devHist.edges,
    },
    reddamNpv: {
      mean: npvs.length > 0 ? sum(npvs) / npvs.length : Number.NaN,
      p5: percentile(npvs, 5),
      p25: percentile(npvs, 25),
      p50: percentile(npvs, 50),
      p75: percentile(npvs, 75),
      p95: percentile(npvs, 95),
      histogram: npvHist.counts,
      bins: npvHist.edges,
    },
    // This financing diagnostic is deliberately evaluated against one cashflow-equivalent geared
    // reference derived from the BASE SCENARIO. Random operating outcomes do not each invent a new
    // required return. NaN means the baseline calibration was unavailable/provisional.
    hurdleMetPct: Number.isFinite(baseScenarioGearedHurdle)
      ? iterations > 0
        ? hurdleMet / iterations
        : 0
      : Number.NaN,
    hurdleReferenceIrr: baseScenarioGearedHurdle,
    breach: {
      rentAboveCautionPct: iterations > 0 ? breachCaution / iterations : 0,
      rentAboveThresholdPct: iterations > 0 ? breachThreshold / iterations : 0,
      ebitdaNegativeY1Pct: iterations > 0 ? ebitdaNegY1 / iterations : 0,
      ebitdaNegativePostRampPct:
        iterations > 0 ? ebitdaNegPostRamp / iterations : 0,
      dscrBreachPct: iterations > 0 ? dscrBreach / iterations : 0,
    },
  };
}

// ---------- synchronous runner ----------
export function monteCarlo(
  scenario: Scenario,
  variables: MonteCarloVariable[],
  computeCore: (s: Scenario) => ModelResult,
  iterations = 1000,
  baseScenarioGearedHurdle = Number.NaN,
): MonteCarloResult {
  const devIrrs: number[] = [];
  const npvs: number[] = [];
  let hurdleMet = 0;
  let breachCaution = 0,
    breachThreshold = 0,
    ebitdaNegY1 = 0,
    ebitdaNegPostRamp = 0,
    dscrBreach = 0;

  for (let i = 0; i < iterations; i++) {
    const iter = runMonteCarloIteration(
      scenario,
      variables,
      computeCore,
      baseScenarioGearedHurdle,
    );
    if (Number.isFinite(iter.devIrr)) devIrrs.push(iter.devIrr);
    if (Number.isFinite(iter.npv)) npvs.push(iter.npv);
    if (iter.hurdleMet) hurdleMet++;
    if (iter.breachCaution) breachCaution++;
    if (iter.breachThreshold) breachThreshold++;
    if (iter.ebitdaNegY1) ebitdaNegY1++;
    if (iter.ebitdaNegPostRamp) ebitdaNegPostRamp++;
    if (iter.dscrBreach) dscrBreach++;
  }

  return aggregateResults(
    iterations,
    devIrrs,
    npvs,
    hurdleMet,
    breachCaution,
    breachThreshold,
    ebitdaNegY1,
    ebitdaNegPostRamp,
    dscrBreach,
    baseScenarioGearedHurdle,
  );
}

// ---------- async chunked runner ----------
export async function monteCarloChunked(
  scenario: Scenario,
  variables: MonteCarloVariable[],
  computeCore: (s: Scenario) => ModelResult,
  iterations = 1000,
  onProgress?: (completed: number, total: number) => void,
  baseScenarioGearedHurdle = Number.NaN,
): Promise<MonteCarloResult> {
  const devIrrs: number[] = [];
  const npvs: number[] = [];
  let hurdleMet = 0;
  let breachCaution = 0,
    breachThreshold = 0,
    ebitdaNegY1 = 0,
    ebitdaNegPostRamp = 0,
    dscrBreach = 0;

  for (let i = 0; i < iterations; i += MC_BATCH_SIZE) {
    const end = Math.min(i + MC_BATCH_SIZE, iterations);
    for (let j = i; j < end; j++) {
      const iter = runMonteCarloIteration(
        scenario,
        variables,
        computeCore,
        baseScenarioGearedHurdle,
      );
      if (Number.isFinite(iter.devIrr)) devIrrs.push(iter.devIrr);
      if (Number.isFinite(iter.npv)) npvs.push(iter.npv);
      if (iter.hurdleMet) hurdleMet++;
      if (iter.breachCaution) breachCaution++;
      if (iter.breachThreshold) breachThreshold++;
      if (iter.ebitdaNegY1) ebitdaNegY1++;
      if (iter.ebitdaNegPostRamp) ebitdaNegPostRamp++;
      if (iter.dscrBreach) dscrBreach++;
    }
    onProgress?.(end, iterations);
    await new Promise((r) => setTimeout(r, 0));
  }

  return aggregateResults(
    iterations,
    devIrrs,
    npvs,
    hurdleMet,
    breachCaution,
    breachThreshold,
    ebitdaNegY1,
    ebitdaNegPostRamp,
    dscrBreach,
    baseScenarioGearedHurdle,
  );
}
